import { P2GBox, P2GText, P2GContainer, P2GButton, P2GLink } from '@parcel2go/core-ui';
import { Integration } from '~/lib/api/account-linking/types';
import ActionInstructions from '~/apps/account-linking/components/ActionInstructions';
import { Suspense, useState } from 'react';
import { SidePanelContent } from '~/components/sidepanel-section';
import { OnboardingTitle } from '../rules/onboarding-title';
import { OnboardingActionBar } from '../intro/onboarding-action-bar';
import { AppSurface } from '~/components/surface/app-surface';
import QuickCsv from '../../../components/quick-csv';
import JustLinkedScreen from './just-linked';
import ECommerceLogo from '~/apps/account-linking/components/ECommerceLogo';
import { OnboardingScreenContentWrapper } from '../../OnboardingScreenContentWrapper';
import { isLinkOrInstallAction } from '../../../components/is-link-or-install-action';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';

type props = {
  isXs: boolean;
  integration: Integration;
  deferredIntegrations?: string[];
  onComplete: () => void;
  doThisIntegration: (id: string) => void;
  onSkip?: () => void;
};

const CsvMessage = () => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  return (
    <>
      <P2GText variant="subtitle2" component="div">
        {t('SETUP_SALES_CHANNELS_CSV_UPLOADING_TITLE', 'Uploading a CSV')}
      </P2GText>
      <P2GText>
        {t(
          'SETUP_SALES_CHANNELS_CSV_UPLOADING_DESCRIPTION',
          "You can use any csv columns, we'll automatically map them to your orders."
        )}
      </P2GText>
    </>
  );
};

const Message = ({ integration }: { integration: Integration | null }) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  return (
    <>
      <P2GText variant="subtitle2" component="div">
        {t('SETUP_SALES_CHANNELS_ACTION_BAR_LINKING_TITLE', 'Linking your {{integration}} store.', {
          integration: integration.name
        })}
      </P2GText>
      <P2GText>
        {t('SETUP_SALES_CHANNELS_ACTION_BAR_LINKING_DESCRIPTION', "We'll help you set up your first store.")}
      </P2GText>
    </>
  );
};

const Deferred = ({ ids, onClick }: { ids: string[]; onClick: (id: string) => void }) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  if (!ids || ids.length === 0) {
    return null;
  }

  return (
    <P2GBox mt={2}>
      <P2GText variant="body2">
        {t('SETUP_SALES_CHANNELS_LINKING_DEFERRED', 'Link these stores later from your dashboard:')}
      </P2GText>
      <P2GBox display="flex" gap={3} justifySelf="center" mt={2}>
        {ids.map((id) => (
          <P2GBox key={id}>
            <ECommerceLogo integration={id} style={{ height: '40px' }} />
            <P2GLink onClick={() => onClick?.(id)} variant="body2" component="div">
              {t('SETUP_SALES_CHANNELS_LINKING_CONNECT_NOW', 'Connect This Now')}
            </P2GLink>
          </P2GBox>
        ))}
      </P2GBox>
    </P2GBox>
  );
};

const IntegrationScreen = ({
  doThisIntegration,
  deferredIntegrations,
  isXs,
  integration,
  onComplete,
  onSkip
}: props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const [linked, setLinked] = useState(false);

  if (linked) {
    return <JustLinkedScreen isXs={isXs} integration={integration} onDone={onComplete} />;
  }

  if (integration.slug === 'csv') {
    return (
      <>
        <OnboardingScreenContentWrapper>
          <P2GBox textAlign="center">
            <SidePanelContent>
              <OnboardingTitle
                text="Adding <span>{{integration}}</span>"
                translationKey="SETUP_SALES_CHANNELS_LINKING_TITLE"
                values={{ integration: integration.name }}
              />
              <P2GText variant="subtitle1">
                {t(
                  'SETUP_SALES_CHANNELS_LINKING_SUBTITLE',
                  'Your other stores can be set up later from the dashboard.'
                )}
              </P2GText>
            </SidePanelContent>
          </P2GBox>
          <P2GContainer maxWidth="sm">
            <P2GBox mt={4}>
              <ECommerceLogo integration={integration.slug} style={{ height: '70px', maxWidth: '100%' }} />
            </P2GBox>
            <P2GBox mt={4}>
              <AppSurface>
                <QuickCsv onComplete={() => setLinked(true)} />
              </AppSurface>
            </P2GBox>
            <Deferred onClick={doThisIntegration} ids={deferredIntegrations || []} />
            {isXs && (
              <P2GBox mt={2}>
                <AppSurface>
                  <P2GBox textAlign="center">
                    <CsvMessage />
                  </P2GBox>
                </AppSurface>
              </P2GBox>
            )}
          </P2GContainer>
        </OnboardingScreenContentWrapper>
        <OnboardingActionBar
          skip={
            <P2GButton variant="text" onClick={onSkip}>
              {t('SKIP_DO_LATER_BUTTON', { defaultValue: "Skip, I'll do this later" })}
            </P2GButton>
          }
        >
          {!isXs && <CsvMessage />}
        </OnboardingActionBar>
      </>
    );
  }

  const canAddMore =
    !integration.maxAccounts || (integration.accounts && integration.accounts.length < integration.maxAccounts);

  return (
    <>
      <OnboardingScreenContentWrapper>
        <P2GBox textAlign="center">
          <SidePanelContent>
            <OnboardingTitle
              text="Adding <span>{{integration}}</span>"
              translationKey="SETUP_SALES_CHANNELS_LINKING_TITLE"
              values={{ integration: integration.name }}
            />
            <P2GText variant="subtitle1">
              {t('SETUP_SALES_CHANNELS_LINKING_SUBTITLE', 'Your other stores can be set up later from the dashboard.')}
            </P2GText>
          </SidePanelContent>
        </P2GBox>
        <P2GContainer maxWidth="sm">
          <P2GBox mt={4}>
            <ECommerceLogo integration={integration.slug} style={{ height: '70px', maxWidth: '100%' }} />
          </P2GBox>
          <P2GBox mt={4}>
            <AppSurface>
              {canAddMore ? (
                <Suspense>
                  <P2GBox textAlign="left">
                    <ActionInstructions
                      configRequired={false}
                      action={integration.accountActions.filter((x) => isLinkOrInstallAction(x))[0]}
                      integrationSlug={integration.slug}
                      integrationType={integration.type}
                      onComplete={() => setLinked(true)}
                      accountId={null}
                    />
                  </P2GBox>
                </Suspense>
              ) : (
                <P2GText>
                  {t('SETUP_SALES_CHANNELS_LINKING_MAX_ACCOUNTS_REACHED', {
                    defaultValue:
                      "You've reached the maximum number of accounts for {{integrationName}}. Please contact support to add more.",
                    integrationName: integration.name
                  })}
                </P2GText>
              )}
            </AppSurface>
          </P2GBox>
          <Deferred onClick={doThisIntegration} ids={deferredIntegrations || []} />
          {isXs && (
            <P2GBox mt={2}>
              <AppSurface>
                <P2GBox textAlign="center">
                  <Message integration={integration} />
                </P2GBox>
              </AppSurface>
            </P2GBox>
          )}
        </P2GContainer>
      </OnboardingScreenContentWrapper>
      <OnboardingActionBar
        skip={
          <P2GButton variant="text" onClick={onSkip}>
            {t('SKIP_DO_LATER_BUTTON', { defaultValue: "Skip, I'll do this later" })}
          </P2GButton>
        }
      >
        {!isXs && <Message integration={integration} />}
      </OnboardingActionBar>
    </>
  );
};
export default IntegrationScreen;
